import { useState, useEffect } from 'react';
import { fetchFacility, fetchSports, fetchEvents } from '@/lib/api';
import SEO from '@/components/SEO';
import { Helmet } from 'react-helmet-async';
import { CricketBallIcon, BaseballIcon, SoftballIcon, SoccerBallIcon, PickleballIcon, BoxCricketIcon } from '@/components/SportIcons';
import { HeroSection, WhatWeDoSection, UpcomingEventsSection, FacilityStatsSection, VisitUsSection, CtaSection } from '@/components/HomeSections';

const SPORT_ICON_MAP = {
  cricket: CricketBallIcon,
  baseball: BaseballIcon,
  softball: SoftballIcon,
  'soccer-futsal': SoccerBallIcon,
  pickleball: PickleballIcon,
  'box-cricket': BoxCricketIcon,
};

function LocalBusinessJsonLd({ facility }) {
  const f = facility || {};
  const data = {
    "@context": "https://schema.org",
    "@type": "SportsActivityLocation",
    "name": f.name || "Propel Sports NC",
    "description": f.description || "Indoor multi-sport facility in Apex, NC. Birthday parties, batting tunnels, soccer, futsal, and tournaments.",
    "url": "https://www.propelsportsnc.com",
    "telephone": f.phone || "(919) 267-0943",
    "email": f.email || "info@propelsportsnc.com",
    "address": { "@type": "PostalAddress", "streetAddress": "810 Center St, Building #4", "addressLocality": "Apex", "addressRegion": "NC", "postalCode": "27502", "addressCountry": "US" },
    "geo": { "@type": "GeoCoordinates", "latitude": "35.7327", "longitude": "-78.8503" },
    "openingHoursSpecification": [
      { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Saturday", "Sunday"], "opens": "08:00", "closes": "22:00" },
      { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], "opens": "06:00", "closes": "22:00" }
    ],
    "image": "https://customer-assets.emergentagent.com/job_multi-sport-hub-7/artifacts/cr0r96t8_Propel%20-%20Orange_refined.png",
    "priceRange": "$40-$460",
    "sameAs": ["https://www.facebook.com/propelsportsnc", "https://www.instagram.com/propelsportsnc", "https://www.linkedin.com/propel-sports"]
  };
  return <Helmet><script type="application/ld+json">{JSON.stringify(data)}</script></Helmet>;
}

function SportsBar({ sports }) {
  return (
    <section data-testid="sports-section" className="py-16 bg-white">
      <div className="max-w-[1000px] mx-auto px-4">
        <div className="grid grid-cols-3 md:grid-cols-6 gap-8 md:gap-10">
          {sports.map(sport => {
            const IconComp = SPORT_ICON_MAP[sport.id] || CricketBallIcon;
            return (
              <div key={sport.id} data-testid={`sport-${sport.id}`} className="flex flex-col items-center gap-3 group cursor-pointer">
                <div className="w-[72px] h-[72px] rounded-full bg-gray-100 flex items-center justify-center group-hover:bg-gray-200 transition-all duration-300">
                  <IconComp className="w-9 h-9" />
                </div>
                <span className="text-[#333] text-[13px] font-medium text-center group-hover:text-[#F26522] transition-colors">{sport.name}</span>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const [facility, setFacility] = useState(null);
  const [sports, setSports] = useState([]);
  const [events, setEvents] = useState([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchFacility().then(setFacility).catch(() => {});
    fetchSports().then(setSports).catch(() => {});
    fetchEvents().then(setEvents).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <SEO path="/" />
      <LocalBusinessJsonLd facility={facility} />
      <HeroSection />
      <SportsBar sports={sports} />
      <WhatWeDoSection />
      <UpcomingEventsSection events={events} />
      <FacilityStatsSection stats={facility?.facility_stats} />
      <VisitUsSection hours={facility?.hours || {}} />
      <CtaSection />
    </div>
  );
}
